/**
 * @shared-compat: Existing Admin, Client, Alice, and Worker flows share this vocabulary.
 *
 * Shared ENT_Websites ("Node") field model.
 *
 * ENT_Websites contains public websites, portals, apps, databases, and forms.
 * Keeping this vocabulary in one package prevents the Admin portal, Client
 * portal, Alice, and the research worker from drifting into WordPress-only
 * assumptions.
 */

export type NodeFieldGroup = 'core' | 'branding' | 'marketing' | 'hosting' | 'wordpress';
export type NodeFieldInput = 'text' | 'multiline' | 'url' | 'email' | 'color' | 'select' | 'lookup';
export type NodeApplicability = 'all' | 'public_marketing' | 'wordpress';

export type NodeFieldKey =
  | 'friendlyName'
  | 'url'
  | 'companyName'
  | 'supportEmail'
  | 'type'
  | 'purpose'
  | 'status'
  | 'organizationId'
  | 'organizationName'
  | 'instructions'
  | 'logoPrimaryUrl'
  | 'logoSecondaryUrl'
  | 'faviconUrl'
  | 'ogImageUrl'
  | 'mainColor'
  | 'accentColor'
  | 'linkColor'
  | 'businessShortDescription'
  | 'businessTagline'
  | 'aboutUsParagraph'
  | 'phoneNumber'
  | 'address'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'openingHours'
  | 'serviceArea'
  | 'heroTitle'
  | 'heroSubtitle'
  | 'whyChooseUsSummary'
  | 'primaryCtaLabel'
  | 'primaryCtaUrl'
  | 'secondaryCtaLabel'
  | 'secondaryCtaUrl'
  | 'mainService'
  | 'servicesDescription'
  | 'subpageHeader'
  | 'seoTitle'
  | 'metaDescription'
  | 'hostingPlatform'
  | 'hostedBy'
  | 'hostingPrice'
  | 'domainName'
  | 'wpAdminUrl'
  | 'wpWebhooksEndpoint'
  | 'wpAppPasswordId'
  | 'parentTheme';

export interface NodeFieldDefinition {
  key: NodeFieldKey;
  label: string;
  airtableField: string;
  group: NodeFieldGroup;
  input: NodeFieldInput;
  applicability: NodeApplicability;
  editable: boolean;
  researchable: boolean;
}

function field(
  key: NodeFieldKey,
  label: string,
  airtableField: string,
  group: NodeFieldGroup,
  input: NodeFieldInput,
  options: Partial<Pick<NodeFieldDefinition, 'applicability' | 'editable' | 'researchable'>> = {},
): NodeFieldDefinition {
  return {
    key,
    label,
    airtableField,
    group,
    input,
    applicability: options.applicability ?? 'all',
    editable: options.editable ?? true,
    researchable: options.researchable ?? false,
  };
}

export const NODE_FIELD_DEFINITIONS: readonly NodeFieldDefinition[] = [
  field('friendlyName', 'Website Friendly Name', 'Website Friendly Name', 'core', 'text', {
    researchable: true,
  }),
  field('url', 'Website URL', 'Website URL', 'core', 'url', { researchable: true }),
  field('companyName', 'Company Name', 'Company Name', 'core', 'lookup', { editable: false }),
  field('supportEmail', 'Support Email', 'Support Email', 'core', 'email', { researchable: true }),
  field('type', 'Type', 'Type', 'core', 'select'),
  field('purpose', 'Purpose', 'Purpose', 'core', 'multiline'),
  field('status', 'Status', 'Status', 'core', 'select'),
  field('organizationId', 'Organization Link', 'Organization Link', 'core', 'lookup'),
  field('organizationName', 'Organization', 'Company Name', 'core', 'lookup', { editable: false }),
  field('instructions', 'Instructions', 'Instructions', 'core', 'multiline'),

  field('logoPrimaryUrl', 'Logo Primary URL', 'Logo Primary URL', 'branding', 'url', {
    researchable: true,
  }),
  field('logoSecondaryUrl', 'Logo Secondary URL', 'Logo Secondary URL', 'branding', 'url', {
    researchable: true,
  }),
  field('faviconUrl', 'Favicon URL', 'Favicon URL', 'branding', 'url', { researchable: true }),
  field('ogImageUrl', 'OG Image URL', 'OG Image URL', 'branding', 'url', { researchable: true }),
  field('mainColor', 'Main Color', 'Main Color (HEX)', 'branding', 'color', { researchable: true }),
  field('accentColor', 'Accent Color', 'Accent Color (HEX)', 'branding', 'color', {
    researchable: true,
  }),
  field('linkColor', 'Link Color', 'Link Color (HEX)', 'branding', 'color', { researchable: true }),

  field(
    'businessShortDescription',
    'Business Short Description',
    'Business Short Description',
    'marketing',
    'multiline',
    { applicability: 'public_marketing', researchable: true },
  ),
  field('businessTagline', 'Business Tagline', 'Business Tagline', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('aboutUsParagraph', 'About Us Paragraph', 'About Us Paragraph', 'marketing', 'multiline', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('phoneNumber', 'Phone Number', 'Phone Number', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('address', 'Address', 'Address', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('city', 'City', 'City', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('state', 'State', 'State', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('zipCode', 'ZIP Code', 'ZIP Code', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('openingHours', 'Opening Hours', 'Opening Hours', 'marketing', 'multiline', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('serviceArea', 'Service Area', 'Service Area', 'marketing', 'multiline', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('heroTitle', 'Hero Title', 'Hero Title', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('heroSubtitle', 'Hero Subtitle', 'Hero Subtitle', 'marketing', 'multiline', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field(
    'whyChooseUsSummary',
    'Why Choose Us Summary',
    'Why Choose Us Summary',
    'marketing',
    'multiline',
    { applicability: 'public_marketing', researchable: true },
  ),
  field('primaryCtaLabel', 'Primary CTA Label', 'Primary CTA Label', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('primaryCtaUrl', 'Primary CTA URL', 'Primary CTA URL', 'marketing', 'url', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('secondaryCtaLabel', 'Secondary CTA Label', 'Secondary CTA Label', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('secondaryCtaUrl', 'Secondary CTA URL', 'Secondary CTA URL', 'marketing', 'url', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('mainService', 'Main Service', 'Main Service', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field(
    'servicesDescription',
    'Services Description',
    'Services Description',
    'marketing',
    'multiline',
    { applicability: 'public_marketing', researchable: true },
  ),
  field('subpageHeader', 'Subpage Header', 'Subpage Header', 'marketing', 'url', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('seoTitle', 'SEO Title', 'SEO Title', 'marketing', 'text', {
    applicability: 'public_marketing',
    researchable: true,
  }),
  field('metaDescription', 'Meta Description', 'Meta Description', 'marketing', 'multiline', {
    applicability: 'public_marketing',
    researchable: true,
  }),

  field('hostingPlatform', 'Hosting Platform', 'Hosting Platform', 'hosting', 'text'),
  field('hostedBy', 'Who Hosts', 'WhoHosts', 'hosting', 'text'),
  field('hostingPrice', 'Hosting Price', 'Hosting Price', 'hosting', 'text', { editable: false }),
  field('domainName', 'Domain Name', 'Domain Name', 'hosting', 'text', { researchable: true }),

  field('wpAdminUrl', 'WordPress Admin URL', 'WP Admin URL', 'wordpress', 'url', {
    applicability: 'wordpress',
    editable: false,
  }),
  field(
    'wpWebhooksEndpoint',
    'WordPress Webhooks Endpoint',
    'WP Webhooks Endpoint',
    'wordpress',
    'url',
    { applicability: 'wordpress' },
  ),
  field('wpAppPasswordId', 'WordPress App Password ID', 'WP App Password ID', 'wordpress', 'text', {
    applicability: 'wordpress',
  }),
  field('parentTheme', 'Parent Theme', 'Parent Theme', 'wordpress', 'text', {
    applicability: 'wordpress',
  }),
] as const;

export const NODE_FIELD_BY_KEY = Object.fromEntries(
  NODE_FIELD_DEFINITIONS.map((definition) => [definition.key, definition]),
) as Record<NodeFieldKey, NodeFieldDefinition>;

export const NODE_TYPE_OPTIONS = [
  'Wordpress Website',
  'Internal Portal',
  'External Portal',
  'Database',
  'Custom App',
  'Universal Canvas App',
  'Advanced Form',
  'source_system',
  'Gravity',
  'Auxilia',
] as const;

export interface NodeClassification {
  publicMarketing: boolean;
  wordpressCompatible: boolean;
  portalOrApp: boolean;
  formOrOther: boolean;
}

export function classifyNode(type?: string, purpose?: string): NodeClassification {
  const haystack = `${type ?? ''} ${purpose ?? ''}`.toLowerCase();
  const wordpressCompatible = /wordpress|structured\s+site/.test(haystack);
  const portalOrApp = /portal|custom\s+app|canvas\s+app|database|source_system/.test(haystack);
  const formOrOther = /form|gravity|auxilia/.test(haystack);
  const explicitlyInternal = /internal|staff|admin|operations|database|source_system/.test(
    haystack,
  );
  const explicitlyPublic =
    /marketing|brochure|business website|lead generation|structured\s+site/.test(haystack);
  const publicMarketing = explicitlyPublic || (wordpressCompatible && !explicitlyInternal);
  return { publicMarketing, wordpressCompatible, portalOrApp, formOrOther };
}

export function nodeFieldIsRelevant(
  definition: NodeFieldDefinition,
  type?: string,
  purpose?: string,
): boolean {
  if (definition.applicability === 'all') return true;
  const classification = classifyNode(type, purpose);
  if (definition.applicability === 'wordpress') return classification.wordpressCompatible;
  return classification.publicMarketing;
}

export function visibleNodeFields(type?: string, purpose?: string): NodeFieldDefinition[] {
  return NODE_FIELD_DEFINITIONS.filter((definition) =>
    nodeFieldIsRelevant(definition, type, purpose),
  );
}

export const NODE_EDITABLE_FIELD_KEYS = NODE_FIELD_DEFINITIONS.filter(
  (definition) => definition.editable,
).map((definition) => definition.key) as NodeFieldKey[];
